import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import moment from 'moment';
import { finalize, from, map, mergeMap } from 'rxjs';

import { Teams } from '../schemas';
import { Jira, UserService } from '../service';

const businessDaysDiff = (x: moment.Moment, y: moment.Moment) => {
  const days = x.diff(y, 'days') + 1;
  // Calculate weekends: 2 days per full week, plus adjustments for partial weeks
  const weekends = 2 * Math.floor((y.day() + days) / 7) + Number(y.day() === 0) - Number(x.day() === 6);
  return days - weekends;
};

interface Row {
  cap: number;
  total_time: number;
  chargeable_time: number;
  details: MemberRow[];
}
interface MemberRow {
  name: string;
  total_time: number;
  chargeable_time: number;
  cap: number;
  individual: boolean;
  team: string;
}

type TimeRecord = [number, number, number, string, string];

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lcm-team-capacity-table',
  imports: [
    MatButtonModule,
    MatToolbarModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    DecimalPipe,
    FormsModule,
    MatTableModule,
  ],
  templateUrl: './team-capacity-table.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    `,
  ],
})
export class TeamCapacityTable {
  pending = signal(false);

  columnsToShow = signal(['name', 'total_time', 'chargeable_time', 'cap', 'rate', 'diff', 'actions']);

  private jira = inject(Jira);
  private userService = inject(UserService);

  StartDate = signal(this.jira.lastCheckpoint().subtract(6, 'days'));
  EndDate = signal(this.jira.lastCheckpoint());

  private recordStream = resource({
    params: () => ({ start: this.StartDate(), end: this.EndDate(), users: this.userService.users() }),
    defaultValue: [],
    stream: async ({ params }) => {
      if (!params.start || !params.end) {
        return signal({ value: [] });
      }
      this.pending.set(true);

      const result = signal({ value: [] as TimeRecord[] });

      from(params.users.filter((user) => user.Team && user.State === 'active'))
        .pipe(
          // Process all users in parallel
          mergeMap(({ Email, Capacity, Team, FullName, JiraId }) =>
            this.jira.getUserTime(params.start, params.end, JiraId || Email).pipe(
              map(
                (t) =>
                  [
                    +(t.total_time / 3600).toFixed(2), // Convert seconds to hours
                    +(t.chargeable_time / 3600).toFixed(2),
                    // Capacity = (weekly capacity / 5 days) * business days in range
                    +(((Capacity || 0) / 5) * businessDaysDiff(params.end, params.start)).toFixed(2),
                    Team,
                    FullName,
                  ] as TimeRecord,
              ),
            ),
          ),
          finalize(() => this.pending.set(false)),
        )
        .subscribe({
          next: (rec) =>
            result.update(({ value }) => ({
              value: [...value, rec],
            })),
        });

      return result;
    },
  });

  private teamReportResource = resource({
    params: () => ({ records: this.recordStream.value() }),
    defaultValue: {},
    loader: async ({ params }) => {
      return params.records.reduce(
        (result, [total_time, chargeable_time, cap, team, name]) => {
          const row: Row = result[team] || {
            cap: 0,
            total_time: 0,
            chargeable_time: 0,
            details: [],
          };
          // Accumulate team totals
          row.cap += cap;
          row.chargeable_time += chargeable_time;
          row.total_time += total_time;
          // Find or create user row within team
          let userRow = row.details.find(({ name: n }) => name === n);
          if (!userRow) {
            userRow = { name, cap: 0, total_time: 0, chargeable_time: 0, individual: true, team: team };
            row.details = [...row.details, userRow];
          }
          // Accumulate user totals
          userRow.cap += cap;
          userRow.chargeable_time += chargeable_time;
          userRow.total_time += total_time;

          result[team] = row;

          return result;
        },
        {} as Record<string, Row>,
      );
    },
  });

  private teamReport = computed<Record<string, Row>>(() => this.teamReportResource.value());

  source = computed(() => {
    const data: MemberRow[] = [];
    const report = this.teamReport();

    for (const [key, name] of Object.entries(Teams)) {
      const row = {
        name,
        total_time: 0,
        chargeable_time: 0,
        cap: 0,
        individual: false,
        team: key,
      };
      data.push(row);

      const team = report[key];
      if (!team) continue;

      row.total_time = team.total_time;
      row.chargeable_time = team.chargeable_time;
      row.cap = team.cap;

      for (const user of team.details) {
        data.push({ ...user, individual: true, team: key });
      }
    }

    const source = new MatTableDataSource(data);
    return source;
  });

  trackBy(idx: number, row: MemberRow) {
    return row.name;
  }

  expanded = signal({});

  toggleRow(key: string) {
    this.expanded.update((value) => ({ ...value, [key]: !value[key] }));
  }
}
